/**
 * ViewValue — hiển thị giá trị PHỨC (object / mảng object / mảng giá trị) trên trang xem và ô bảng.
 *
 * LỖI ĐÃ GẶP: `formatDisplayValue` rơi xuống nhánh mặc định `String(value)` nên field chứa object in ra
 * "[object Object]", mảng object in ra "[object Object],[object Object]". Hay gặp nhất với cấu hình dịch
 * từ listinfo (field `Mixed`/`String` mà dữ liệu thật là object) và với lưới chi tiết khai thiếu
 * `subFields`.
 *
 * Chọn cách vẽ theo DỮ LIỆU THẬT (viewTabsRules.viewValueShape), không chỉ theo `type` khai báo.
 * KHÔNG import DataListItems/ObjectArrayControl (hai file đó import ngược lại file này) — định dạng ô
 * nhận qua prop `formatCell`.
 */
import React from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { viewValueShape, inferColumns, isEmptyViewValue } from './viewTabsRules';

const MAX_DEPTH = 3;
const LABEL_KEYS = ['ten', 'name', 'label', 'title', 'ten_kh', 'ten_vt', 'ten_nv', 'ten_trang_thai', 'value', 'ma'];

/** Tóm tắt MỘT dòng chữ cho ô bảng / thẻ danh sách (không vẽ bảng lồng trong ô). */
export function summarizeValue(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    if (value.some(x => x && typeof x === 'object' && !(x instanceof Date))) return `${value.length} dòng`;
    return value.map(v => summarizeValue(v)).filter(Boolean).join(', ');
  }
  if (value instanceof Date) return value.toLocaleDateString('vi-VN');
  if (typeof value === 'object') {
    const k = LABEL_KEYS.find(key => value[key] !== undefined && value[key] !== null && value[key] !== '' && typeof value[key] !== 'object');
    if (k) return String(value[k]);
    const s = Object.keys(value).filter(key => !isEmptyViewValue(value[key]))
      .map(key => `${key}: ${typeof value[key] === 'object' ? summarizeValue(value[key]) : value[key]}`).join('; ');
    return s.length > 120 ? s.slice(0, 117) + '…' : s;
  }
  return String(value);
}

/** Kiểu cho một cột SUY RA (không khai subFields) — để số/ngày vẫn được định dạng. */
function guessField(key, sample) {
  if (typeof sample === 'number') return { field: key, label: key, type: 'Number' };
  if (typeof sample === 'boolean') return { field: key, label: key, type: 'Boolean' };
  if (typeof sample === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(sample)) return { field: key, label: key, type: 'Date' };
  return { field: key, label: key, type: 'String' };
}

function Cell({ value, field, row, formatCell, depth }) {
  if (isEmptyViewValue(value)) return <span style={{ opacity: 0.4 }}>—</span>;
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    // Lồng sâu trong bảng: chỉ tóm tắt một dòng, bảng trong bảng là không đọc nổi.
    if (depth >= 1 || Array.isArray(value)) return <span>{summarizeValue(value)}</span>;
  }
  if (typeof formatCell === 'function') return formatCell(value, field, row);
  return <span>{String(value)}</span>;
}

/** Bảng chỉ đọc cho mảng object. */
function DetailTable({ rows, field, formatCell, depth }) {
  const declared = Array.isArray(field?.subFields) && field.subFields.length
    ? field.subFields.filter(f => f && f.field && f.type !== 'Action' && !(f.hideInList === true))
    : null;
  const cols = declared || inferColumns(rows, 12).map(c => guessField(c.field, (rows.find(r => r && r[c.field] !== undefined) || {})[c.field]));
  if (!cols.length) return <Typography variant="body2" color="text.secondary">{rows.length} dòng</Typography>;
  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 0.5 }}>
      <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', fontSize: '0.8125rem' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ width: 40, fontWeight: 700 }}>#</TableCell>
            {cols.map(c => <TableCell key={c.field} sx={{ fontWeight: 700 }} align={c.type === 'Number' ? 'right' : 'left'}>{c.label || c.field}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={(r && (r._id || r.line)) || i}>
              <TableCell>{i + 1}</TableCell>
              {cols.map(c => (
                <TableCell key={c.field} align={c.type === 'Number' ? 'right' : 'left'}>
                  {r && typeof r === 'object'
                    ? <Cell value={r[c.field]} field={c} row={r} formatCell={formatCell} depth={depth + 1} />
                    : (c === cols[0] ? String(r) : null)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** Danh sách cặp nhãn / giá trị cho một object. */
function ObjectList({ obj, field, formatCell, depth }) {
  const declared = Array.isArray(field?.subFields) && field.subFields.length ? field.subFields : null;
  const entries = declared
    ? declared.filter(f => f && f.field).map(f => [f, obj[f.field]])
    : Object.keys(obj).filter(k => k !== '_id' && k !== '__v').map(k => [guessField(k, obj[k]), obj[k]]);
  const shown = entries.filter(([, v]) => !isEmptyViewValue(v));
  if (!shown.length) return <Typography variant="body2" color="text.secondary">---</Typography>;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', columnGap: 2, rowGap: 0.5, mt: 0.5,
      pl: depth ? 1.5 : 0, borderLeft: depth ? '2px solid' : 'none', borderColor: 'divider' }}>
      {shown.map(([f, v]) => (
        <React.Fragment key={f.field}>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{f.label || f.field}</Typography>
          <Box sx={{ minWidth: 0, wordBreak: 'break-word' }}>
            <ViewValue value={v} field={f} row={obj} formatCell={formatCell} depth={depth + 1} />
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

export default function ViewValue({ value, field, row, formatCell, depth = 0 }) {
  if (isEmptyViewValue(value)) return <Typography variant="body1" color="text.secondary">---</Typography>;
  const shape = viewValueShape(value, field);
  if (depth > MAX_DEPTH && shape !== 'scalar') return <Typography variant="body2">{summarizeValue(value)}</Typography>;
  if (shape === 'detail') return <DetailTable rows={value} field={field} formatCell={formatCell} depth={depth} />;
  if (shape === 'object') return <ObjectList obj={value} field={field} formatCell={formatCell} depth={depth} />;
  if (shape === 'chips') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {value.filter(v => !isEmptyViewValue(v)).map((v, i) => <Chip key={i} size="small" label={v instanceof Date ? v.toLocaleDateString('vi-VN') : String(v)} />)}
      </Box>
    );
  }
  if (typeof formatCell === 'function') return formatCell(value, field, row);
  return <Typography variant="body1">{String(value)}</Typography>;
}
